import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import SearchInput from "@/components/ui/search-input";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import Checkbox from "@/components/ui/checkbox";
import FilterMenuItem from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterMenuItem";
import {
  extractFilterValues,
  NO_VALUE,
  useFilterToggleWithNotIn
} from "@/hooks/useFilterOptions";

interface CustomFieldOption {
  value: string | null;
  label: string;
  isNoValue?: boolean;
}

interface Props {
  filterOption: DataTableFilter;
  filters: Filters;
  setFilters: FiltersSetter;
  onClose?: () => void;
}

const FilterCustomFieldSubmenu: React.FC<Props> = ({
  filterOption,
  filters,
  setFilters,
  onClose
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const filterKey = filterOption.key;

  const customFieldOptions = useMemo((): CustomFieldOption[] => {
    const prefix: CustomFieldOption[] = filterOption.excludeNoValue
      ? []
      : [
          {
            value: NO_VALUE,
            label: t("datatoolbar.filters.noValue"),
            isNoValue: true
          }
        ];
    if (filterOption.filterType.type !== "customField") {
      return prefix;
    }
    const { options } = filterOption.filterType.values;
    return [
      ...prefix,
      ...Object.entries(options).map(([key, value]) => ({
        value: key,
        label: value
      }))
    ];
  }, [filterOption.filterType, filterOption.excludeNoValue, t]);

  // Get currently selected values
  const currentValue = filters[filterKey as keyof Filters];
  const selectedValues = useMemo(() => {
    return extractFilterValues(currentValue);
  }, [currentValue]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return customFieldOptions;
    const noValueOption = customFieldOptions.find((opt) => opt.isNoValue);
    const named = customFieldOptions
      .filter((opt) => !opt.isNoValue)
      .filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
      );
    return noValueOption ? [noValueOption, ...named] : named;
  }, [customFieldOptions, searchValue]);

  const toggleValue = useFilterToggleWithNotIn(filterKey, setFilters);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const shouldShowSearch = customFieldOptions.length > 10;

  return (
    <div className="flex flex-col gap-2 min-w-[200px] max-h-[300px]">
      {shouldShowSearch && (
        <div className="sticky top-0 bg-surface-elevation2 z-10">
          <SearchInput
            placeholder={t("search")}
            value={searchValue}
            onChange={handleSearchChange}
            hideIcon
            clearButtonSize="xs"
            clearButtonColor="secondary"
          />
        </div>
      )}
      <div className="flex flex-col overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <Text variant="bodyTertiary" className="px-2 py-4 text-center">
            {t("tables.emptyState.title")}
          </Text>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <FilterMenuItem
                key={String(option.value)}
                layout="compact"
                onClick={() => {
                  toggleValue(option.value);
                  onClose?.();
                }}
              >
                <Checkbox
                  checked={isSelected}
                  className="mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(option.value);
                  }}
                />
                <Text
                  className={cn(
                    "flex-1",
                    option.isNoValue && "italic text-tertiary"
                  )}
                >
                  {option.label}
                </Text>
              </FilterMenuItem>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FilterCustomFieldSubmenu;
